import { Button, FileButton, Group } from '@mantine/core';
import { closeAllModals } from '@mantine/modals';
import { IconDownload, IconUpload } from '@tabler/icons-react';
import { SuperButton } from 'components/SuperButton';
import { i18nKeys, useTranslation } from 'locales';
import { useState } from 'react';
import { Document, Page } from 'react-pdf';
import { isDefined, isNullish } from 'remeda';
import { UploadZone } from 'shared/io';
import { creditNoteActions } from 'store/creditNote/creditnote.actions';
import { invoiceActions } from 'store/invoice/invoice.actions';
import type { Invoice } from 'types/store/invoice-state';

type InvoicePdfModalProps = {
  isCreditNote: boolean;
  invoice: Invoice;
  onEditOriginalFile: Function;
};

export const InvoicePdfModal = ({
  isCreditNote,
  invoice,
  onEditOriginalFile,
}: InvoicePdfModalProps) => {
  const { t } = useTranslation();

  const [pdfFile, setPdfFile] = useState<File | null>(null);

  const downloadPdf = () => {
    invoiceActions.document({
      id: invoice!.id,
      type: 'invoice_pdf',
    });
  };

  const actions = invoice.type === 'credit_note' ? creditNoteActions : invoiceActions;

  const reloadInfo = () => {
    if (isCreditNote) creditNoteActions.info({ id: invoice.id, noReset: true, noLoading: true });
    else invoiceActions.info({ id: invoice.id, noReset: true, noLoading: true });
  };

  const submit = () => {
    actions.updateOriginalFile({
      id: invoice.id,
      data: { file: pdfFile },
      callback: () => {
        onEditOriginalFile();
        reloadInfo();
        closeAllModals();
      },
    });
  };

  const replaceFile = (file: File | null) => {
    if (isNullish(file)) return;

    actions.updateOriginalFile({
      id: invoice.id,
      data: { file },
      callback: () => {
        onEditOriginalFile();
        reloadInfo();
        closeAllModals();
      },
    });
  };

  const deleteFile = () => {
    actions.deleteOriginalFile({
      id: invoice.id,
      callback: () => {
        onEditOriginalFile();
        reloadInfo();
        closeAllModals();
      },
    });
  };

  return invoice.has_pdf ? (
    <>
      <Viewer url={invoice.pdf_url} />
      <Group justify="flex-end" gap="md" mt="md" w="100%" pr="0px">
        {/* This permission is active when the invoice has been imported rather than created in Recovr */}
        {invoice.actions.includes('update_pdf') && (
          <FileButton onChange={replaceFile} accept=".pdf">
            {props => (
              <Button
                color="red"
                variant="light"
                leftSection={<IconUpload stroke={1.5} size={18} />}
                {...props}
              >
                {t(i18nKeys.INVOICE.EDIT_ORGINAL_FILE)}
              </Button>
            )}
          </FileButton>
        )}
        <SuperButton onClick={deleteFile}>{t(i18nKeys.DELETE)}</SuperButton>
        <Button leftSection={<IconDownload stroke={1.5} size={18} />} onClick={downloadPdf}>
          {t(i18nKeys.INVOICE.DOWNLOAD_PDF)}
        </Button>
      </Group>
    </>
  ) : (
    <>
      <UploadZone
        noMargin
        accept=".pdf"
        info={t(i18nKeys.INVOICING.IMPORT.ORIGINAL_FILE)}
        subinfo={t(i18nKeys.FORM.FILE_FORMAT_PDF)}
        noPreview
        onChange={setPdfFile}
        value={pdfFile}
        showError
      />
      <Button onClick={submit} disabled={!isDefined(pdfFile)}>
        {t(i18nKeys.IMPORT)}
      </Button>
    </>
  );
};

const Viewer = ({ url }: { url: string | null }) => {
  const [pdfPagesAmount, setPdfPagesAmount] = useState(0);

  return (
    <Document file={url} onLoadSuccess={args => setPdfPagesAmount(args._pdfInfo.numPages)}>
      {Array(pdfPagesAmount)
        .fill(null)
        .map((_, index) => (
          <Page
            renderAnnotationLayer={false}
            renderTextLayer={false}
            key={index}
            height={800}
            pageIndex={index}
          />
        ))}
    </Document>
  );
};
